import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { TbBellRinging, TbSearch, TbMoon, TbSun, TbBook2, TbTrash, TbUser, TbLock } from 'react-icons/tb';
import axios from 'axios';
import { useAuth } from '../../services/check_login';
import '../../../assets/css/header.css';

const Header = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    // Load dark mode preference from localStorage
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'true') {
      setDarkMode(true);
      document.body.classList.add('dark-mode');
    }
    
    // Fetch notifications
    if (currentUser && currentUser.username) {
      fetchNotifications();
      fetchUnreadCount();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchNotifications();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, []);

  // Close notifications dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showNotifications && !e.target.closest('.notification-container')) {
        setShowNotifications(false);
      }
      if (showAccountMenu && !e.target.closest('.account-container')) {
        setShowAccountMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showAccountMenu]);

  const fetchNotifications = async () => {
    if (!currentUser || !currentUser.username) return;
    try {
      const response = await axios.get(`/api/notifications/${currentUser.username}`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    if (!currentUser || !currentUser.username) return;
    try {
      const response = await axios.get(`/api/notifications/${currentUser.username}/unread/count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      await axios.put(`/api/notifications/${notification._id}/read`);
      
      // Update local state
      setNotifications(notifications.map(n => 
        n._id === notification._id ? { ...n, isRead: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      // Navigate to the link
      if (notification.link) {
        setShowNotifications(false);
        navigate(notification.link);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`/api/notifications/${currentUser.username}/read-all`);
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`/api/notifications/${notificationId}`);
      setNotifications(notifications.filter(n => n._id !== notificationId));
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const toggleNotifications = () => {
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const formatNotificationTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return time.toLocaleDateString();
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    
    if (newMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('darkMode', 'false');
    }
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    const dateStr = date.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });

    if (diffMins < 2) return 'Just now';
    if (diffHours < 24) return `Today at ${timeStr}`;
    if (diffDays === 1) return `Yesterday at ${timeStr}`;
    return `${dateStr} at ${timeStr}`;
  };

  const getInitials = () => {
    if (!currentUser) return '';
    const firstName = currentUser.firstName || '';
    const lastName = currentUser.lastName || '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  return (
    <header className="app-header">
      <div className="header-content">
        <div className="search-bar">
          <TbSearch className="search-icon" />
          <input type="text" placeholder="Search..." className="search-input" />
        </div>

        <div className="user-actions">
          <Link to="/documentation" className="documentation-link" title="Documentation">
            <TbBook2 />
          </Link>

          <div className="notification-container">
            <button className="notification-btn" onClick={toggleNotifications}>
              <TbBellRinging />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="notifications-dropdown">
                <div className="notifications-header">
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <button className="mark-all-read" onClick={markAllAsRead}>
                      Mark all as read
                    </button>
                  )}
                </div>
                
                <div className="notifications-list">
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <div
                        key={notification._id}
                        className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="notification-content">
                          <p className="notification-message">
                            {notification.message}
                            {notification.creativeName && (
                              <> in "{notification.creativeName}"</>
                            )}
                          </p>
                          <div className="notification-footer">
                            <span className="notification-time">
                              {formatNotificationTime(notification.createdAt)}
                            </span>
                          </div>
                        </div>
                        <button
                          className="action-icon-btn btn btn-outline-danger btn-sm"
                          onClick={(e) => deleteNotification(notification._id, e)}
                          title="Delete notification"
                        >
                          <TbTrash size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications">No notifications</div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="account-container">
            <button
              className="user-profile"
              onClick={() => setShowAccountMenu(!showAccountMenu)}
            >
              <div className="avatar">
                {getInitials()}
              </div>
            </button>

            {showAccountMenu && (
              <div className="account-dropdown">
                <div className="account-dropdown-header">
                  <div className="avatar avatar-lg">{getInitials()}</div>
                  <div className="account-info">
                    <span className="account-name">
                      {currentUser?.firstName} {currentUser?.lastName}
                    </span>
                    <span className="account-email">{currentUser?.email}</span>
                    {currentUser?.lastLogin && (
                      <span className="account-last-login">
                        Last login: {formatLastLogin(currentUser.lastLogin)}
                      </span>
                    )}
                  </div>
                </div>

                <Link
                  to="/profile"
                  className="account-menu-item"
                  onClick={() => setShowAccountMenu(false)}
                >
                  <TbUser /> Profile
                </Link>

                <div className="account-menu-item account-menu-toggle">
                  <span className="account-menu-toggle-label">
                    {darkMode ? <TbMoon /> : <TbSun />} Dark Mode
                  </span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={darkMode}
                      onChange={toggleDarkMode}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <Link
                  to="/reset-password"
                  className="account-menu-item"
                  onClick={() => setShowAccountMenu(false)}
                >
                  <TbLock /> Reset Password
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;